import { useState, useEffect, useCallback } from "react";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import { FolderKanban, Calendar, Filter } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export default function ArtistProjects() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // active, completed, all
  const [timeFilter, setTimeFilter] = useState("all"); // month, quarter, year, all

  const fetchProjects = useCallback(async () => {
    try {
      // Fetch assignments for current user's artist_id
      const res = await apiClient.get("/artist/my-projects");
      setProjects(res.data || []);
    } catch (error) {
      console.error("Failed to fetch projects:", error);
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const filteredProjects = projects.filter(p => {
    if (filter === "active" && p.status !== "Active") return false;
    if (filter === "completed" && p.status !== "Completed") return false;
    return true;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case "Active": return "bg-green-100 text-green-700";
      case "Completed": return "bg-gray-100 text-gray-700";
      case "On Hold": return "bg-yellow-100 text-yellow-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="artist-projects">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-light" style={{ fontFamily: 'var(--font-sans)' }}>
          My Projects
        </h1>
        <div className="flex items-center gap-2">
          <Select value={timeFilter} onValueChange={setTimeFilter}>
            <SelectTrigger className="w-28 h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="mb-4">
          <TabsTrigger value="all">All ({projects.length})</TabsTrigger>
          <TabsTrigger value="active">Active ({projects.filter(p => p.status === "Active").length})</TabsTrigger>
          <TabsTrigger value="completed">Completed ({projects.filter(p => p.status === "Completed").length})</TabsTrigger>
        </TabsList>

        <TabsContent value={filter}>
          {filteredProjects.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <FolderKanban className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No projects found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredProjects.map(project => (
                <Card key={project.id} className="hover:border-primary/30 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <FolderKanban className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-medium">{project.program_name}</h3>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-0.5">
                            <Calendar className="w-3.5 h-3.5" />
                            <span>{project.start_date} → {project.end_date}</span>
                            {project.pm_name && (
                              <>
                                <span>•</span>
                                <span>PM: {project.pm_name}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <Badge className={getStatusColor(project.status)}>
                        {project.status}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
